import { useNavigate } from 'react-router'
import { Skeleton } from '../../../platform/async/Skeleton'
import type { AsyncState } from '../../../platform/async/Async'
import { PremiumGate } from '../../../platform/access/PremiumGate'
import { Money } from '../../../platform/primitives/Money'
import { sharePercent } from '../../cashflow/cashflowRules'
import { OverviewWidget } from '../OverviewWidget'
import { arcAngles, arcPath } from './donut'
import type { WhereView } from './whereView'

const OUTER = 48
const INNER = 30
/** Series fills are positional (D14): the nth row takes `--s{n}`, set in CSS. */
const SERIES = 4

function Donut({ rows }: { rows: WhereView['rows'] }) {
  const angles = arcAngles(rows.map((r) => r.sharePct))
  return (
    <svg className="ov-donut" viewBox="0 0 100 100" aria-hidden="true" focusable="false">
      {rows.map((r, i) => {
        const d = arcPath(angles[i] ?? { start: 0, end: 0 }, OUTER, INNER)
        return d === '' ? null : <path key={r.categoryId} className={`ov-arc ov-arc--${(i % SERIES) + 1}`} d={d} data-chart />
      })}
    </svg>
  )
}

/** A ring in the real donut's anatomy, with no figure, text or data in it (PremiumGate's preview contract). */
function RingPreview() {
  const shares = [34, 24, 18, 12]
  const angles = arcAngles(shares)
  return (
    <svg className="ov-donut ov-donut--preview" viewBox="0 0 100 100" aria-hidden="true">
      {shares.map((s, i) => (
        <path key={s} className={`ov-arc ov-arc--${i + 1}`} d={arcPath(angles[i] ?? { start: 0, end: 0 }, OUTER, INNER)} />
      ))}
    </svg>
  )
}

/**
 * Where it went: a donut and a four-row legend of the server's top categories.
 * Amounts, shares and their order are the server's; the donut carries no text,
 * so every value lives in the legend (rule 11). A free user sees the locked
 * ring and nothing of their data — the real children are never rendered.
 */
export function WhereItWentWidget({ state, entitled }: { state: AsyncState<WhereView>; entitled: boolean }) {
  const navigate = useNavigate()
  const common = { title: 'Where it went', subtitle: 'This month, by category', href: '/cashflow' } as const
  if (!entitled) {
    return (
      <OverviewWidget<null> {...common} surface="overview-where-it-went" state={{ status: 'success', data: null }} skeleton={null} empty={{ title: '' }}>
        {() => (
          <PremiumGate entitlement="cashflow_details" cta="none" panelKind="categories" preview={<RingPreview />}>
            {null}
          </PremiumGate>
        )}
      </OverviewWidget>
    )
  }
  return (
    <OverviewWidget<WhereView>
      {...common}
      surface="overview-where-it-went"
      state={state}
      skeleton={
        <>
          <Skeleton height={120} width={120} />
          <Skeleton height={13} />
          <Skeleton height={13} />
        </>
      }
      empty={{ title: 'Nothing to break down yet', action: { label: 'See cashflow', onAct: () => void navigate('/cashflow') } }}
    >
      {({ rows, currency, categoryCount }) => (
        <div className="ov-where">
          <Donut rows={rows} />
          <div className="ov-where__legend">
            <ul className="ov-list" aria-label="Top categories">
              {rows.map((r, i) => (
                <li key={r.categoryId} className="ov-list__row">
                  <span className={`ov-swatch ov-swatch--${(i % SERIES) + 1}`} aria-hidden="true" data-chart />
                  <span className="ov-list__name">{r.name}</span>
                  <Money cents={r.amountCents} currency={currency} kind="stock" />
                  <span className="note">{sharePercent(r.sharePct)}</span>
                </li>
              ))}
            </ul>
            <p className="note">{`${categoryCount} ${categoryCount === 1 ? 'category' : 'categories'}`}</p>
          </div>
        </div>
      )}
    </OverviewWidget>
  )
}
